import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  type HTMLMotionProps,
  type Variants,
} from "framer-motion";
import { useEffect, type ReactNode } from "react";
import { useAppearance } from "@/lib/appearance";

type DivMotionProps = Omit<HTMLMotionProps<"div">, "children">;

interface FadeProps extends DivMotionProps {
  delay?: number;
  duration?: number;
  y?: number;
  children?: ReactNode;
}

/**
 * Fade-and-rise on mount. Reduce-motion users get an instant render.
 */
export function MotionFade({
  delay = 0,
  duration = 0.45,
  y = 8,
  children,
  ...rest
}: FadeProps) {
  const { isReducedMotion } = useAppearance();
  if (isReducedMotion) {
    return <div {...(rest as React.HTMLAttributes<HTMLDivElement>)}>{children}</div>;
  }
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration, ease: [0.16, 1, 0.3, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

interface SlideProps extends DivMotionProps {
  delay?: number;
  duration?: number;
  from?: "left" | "right" | "top" | "bottom";
  distance?: number;
  children?: ReactNode;
}

/**
 * Slide-in from a chosen edge.
 */
export function MotionSlide({
  delay = 0,
  duration = 0.5,
  from = "bottom",
  distance = 24,
  children,
  ...rest
}: SlideProps) {
  const { isReducedMotion } = useAppearance();
  if (isReducedMotion) {
    return <div {...(rest as React.HTMLAttributes<HTMLDivElement>)}>{children}</div>;
  }
  const offset =
    from === "left"
      ? { x: -distance, y: 0 }
      : from === "right"
        ? { x: distance, y: 0 }
        : from === "top"
          ? { x: 0, y: -distance }
          : { x: 0, y: distance };
  return (
    <motion.div
      initial={{ opacity: 0, ...offset }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ delay, duration, ease: [0.16, 1, 0.3, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

interface StaggerProps {
  children: React.ReactNode;
  delayBetween?: number;
  initialDelay?: number;
  className?: string;
  asChild?: boolean;
}

/**
 * Wraps a list of children and staggers their fade-in. Each direct child becomes
 * a motion item. Reduce-motion users get an instant render.
 */
export function MotionStagger({
  children,
  delayBetween = 0.06,
  initialDelay = 0.05,
  className,
  ...rest
}: StaggerProps & { [key: `data-${string}`]: string | undefined }) {
  const { isReducedMotion } = useAppearance();
  const items = Array.isArray(children) ? children : [children];

  if (isReducedMotion) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: delayBetween, delayChildren: initialDelay },
    },
  };
  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <motion.div
      className={className}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      {...rest}
    >
      {items.map((child, i) => (
        <motion.div key={i} variants={itemVariants}>
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}

interface MotionCountProps {
  value: number;
  duration?: number;
  format?: (n: number) => string;
  className?: string;
  "data-testid"?: string;
}

/**
 * Animates a number from 0 (or its previous value) to `value`. Reduce-motion
 * users see the final value immediately.
 */
export function MotionCount({
  value,
  duration = 1.0,
  format,
  className,
  ...rest
}: MotionCountProps) {
  const { isReducedMotion } = useAppearance();
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) =>
    format ? format(v) : Math.round(v).toLocaleString(),
  );

  useEffect(() => {
    if (isReducedMotion) {
      mv.set(value);
      return;
    }
    const controls = animate(mv, value, { duration, ease: [0.16, 1, 0.3, 1] });
    return () => controls.stop();
    // Intentional: `mv` is a stable MotionValue instance; re-running on its
    // identity would restart the animation needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isReducedMotion, duration]);

  return (
    <motion.span className={className} {...rest}>
      {display}
    </motion.span>
  );
}
